import { useState } from "react";
import { IonContent, IonPage } from "@ionic/react";

import { BarChart, type PairedDay } from "../components/BarChart";
import { Hero } from "../components/Hero";
import { Insight } from "../components/Insight";
import { Notice } from "../components/Notice";
import { ProgressBar } from "../components/ProgressBar";
import { NavRow, RowGroup } from "../components/Rows";
import { Segmented } from "../components/Segmented";
import { Screen } from "../components/Shell";
import { Tag } from "../components/Tag";
import { TopBar } from "../components/TopBar";
import { I } from "../components/icons";
import { cycleAffected } from "../domain/cycle";
import { addDays, dayLabel, dayMonth, parseDay, todayStr } from "../domain/date";
import { KCAL_PER_KG_FAT } from "../domain/deficit";
import { nutrientAverages, proteinByMeal, type NutrientKey } from "../domain/foodTrends";
import { WINDOW_LABEL, type Insight as InsightData, type Window } from "../domain/insight";
import { foodOn } from "../domain/intake";
import type { DayLedger } from "../domain/ledger";
import { energyInsights, energyView, signed } from "../domain/progressView";
import type { KayaState } from "../domain/types";
import { useGo } from "../lib/useGo";
import { useKaya } from "../store/kayaStore";
import "./Progress.css";

const WINDOWS: Window[] = [28, 90, 0];
/** Past this many days a pair a day is under 3px wide and the pairs overprint; weeks instead. */
const DAILY_MAX = 31;

const fmt = (n: number) => Math.round(n).toLocaleString("en-US");

function avgOf(xs: (number | null)[]): number | null {
  const k = xs.filter((x): x is number => x !== null);
  return k.length ? Math.round(k.reduce((a, b) => a + b, 0) / k.length) : null;
}

/** The window as chart pairs: a pair a day up to a month, then a pair a week (weekly means). */
function chartDays(days: DayLedger[]): { pairs: PairedDay[]; weekly: boolean } {
  if (days.length <= DAILY_MAX) {
    const last = days.length - 1;
    return {
      weekly: false,
      pairs: days.map((d, i) => ({
        key: d.ds,
        // One label a week: 28 dates side by side would overprint each other.
        label: i === last ? String(parseDay(d.ds).getDate()) : i % 7 === 0 && last - i >= 3 ? dayMonth(d.ds) : "",
        name: dayLabel(d.ds),
        in: d.in,
        out: d.out,
      })),
    };
  }
  // Weeks counted back from the newest day, so the last pair is a whole week.
  const weeks: DayLedger[][] = [];
  for (let end = days.length; end > 0; end -= 7) weeks.unshift(days.slice(Math.max(0, end - 7), end));
  const last = weeks.length - 1;
  return {
    weekly: true,
    pairs: weeks.map((wk, i) => ({
      key: wk[0].ds,
      label: i === last || (i % 4 === 0 && last - i >= 3) ? dayMonth(wk[0].ds) : "",
      name: `Week of ${dayMonth(wk[0].ds)}`,
      in: avgOf(wk.map((d) => d.in)),
      out: avgOf(wk.map((d) => d.out)),
    })),
  };
}

/** "Protein 94 of 122 g · iron is the gap" — Food's own hero and weakest nutrient, for the link. */
function foodLine(S: KayaState, end: string, w: Window): string {
  const pm = proteinByMeal(S, end, w);
  if (pm.avg === null) return `No food logged in ${w === 0 ? "the record" : WINDOW_LABEL[w]}`;
  const na = nutrientAverages(S, end, w);
  const weakest = (["fb", "fe", "ca", "pot"] as NutrientKey[])
    .map((k) => na.by[k])
    .filter((r) => r.known >= 7 && r.avg !== null && r.target !== null && r.target > 0)
    .map((r) => ({ label: r.label, ratio: (r.avg ?? 0) / (r.target ?? 1) }))
    .sort((a, b) => a.ratio - b.ratio)[0];
  const protein = `Protein ${fmt(pm.avg)} of ${pm.target} g`;
  return weakest && weakest.ratio < 1 ? `${protein} · ${weakest.label.toLowerCase()} is the gap` : protein;
}

/**
 * /progress/energy — In against Out over a window that ends yesterday (a half-logged today drags
 * an average), what that should have done to the scale against what the scale did, the burn the
 * scale implies, where the deficit comes from, and how much of the window the food figures rest
 * on. Food off: the hero says In is unknown and the food-built groups collapse to one Notice.
 */
export default function ProgressEnergy() {
  const S = useKaya((s) => s.S);
  const go = useGo();
  const [w, setW] = useState<Window>(28);

  const today = todayStr();
  const end = addDays(today, -1);
  const affected = (ds: string) => cycleAffected(S, ds);
  // energyView leaves today out itself, so its window already ends yesterday.
  const v = energyView(S, today, w, affected);
  const all = energyInsights(S, today, w, affected);
  const pick = (key: string): InsightData | undefined => all.find((i) => i.key === key);
  const food = foodOn(S);

  const { pairs, weekly } = chartDays(v.days);
  // The words under the first pair only fit a week of full-width pairs, and name an In series.
  const words = food && pairs.length <= 7;
  const kgWeek = v.deficit === null ? null : (v.deficit * 7) / KCAL_PER_KG_FAT;

  const segmented = (
    <div className="pg-hero__switch">
      <Segmented<Window>
        options={WINDOWS.map((x) => ({ value: x, label: WINDOW_LABEL[x] }))}
        value={w}
        onChange={setW}
        ariaLabel="Window"
      />
    </div>
  );

  const heroLine = !food
    ? `Food logging is off, so In is not known${v.outAvg !== null ? ` · Out ~${fmt(v.outAvg)} kcal a day est.` : ""}`
    : v.outAvg === null
      ? "Out needs your year of birth and sex"
      : v.paired === 0
        ? `No food logged ${w === 0 ? "since you started" : `in ${WINDOW_LABEL[w]}`}`
        : `${v.paired} logged ${v.paired === 1 ? "day" : "days"}${
            kgWeek !== null ? ` · ≈ ${signed(kgWeek, 2)} kg/week if it held` : ""
          }`;

  const agree = pick("energy.agree");
  const implied = pick("energy.implied");
  const split = pick("energy.split");
  const stall = pick("energy.stall");
  const cover = pick("energy.coverage");

  const splitFood = v.split ? Math.max(0, v.split.food) : 0;
  const splitWork = v.split ? Math.max(0, v.split.work) : 0;

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <TopBar title="Energy" back={{ to: "/progress", label: "Back to Progress" }} />
          <div className="pg">
            <Hero
              label={`In vs Out · ${WINDOW_LABEL[w]}`}
              figure={food && v.deficit !== null ? signed(v.deficit) : null}
              unit={
                <>
                  kcal a day <Tag tone="est" />
                </>
              }
              line={heroLine}
              visual={
                <>
                  <BarChart
                    mode="paired"
                    days={pairs}
                    per={weekly ? "week" : "day"}
                    avg={food ? v.inAvg : null}
                    outEstimated
                    words={words}
                    ariaLabel={`In and Out ${weekly ? "by week" : "by day"}, ${
                      w === 0 ? "since you started" : `the ${WINDOW_LABEL[w]}`
                    } to yesterday. Average In ${v.inAvg === null ? "unknown" : fmt(v.inAvg)}, average Out about ${
                      v.outAvg === null ? "unknown" : fmt(v.outAvg)
                    } kcal.`}
                    empty="Nothing to show yet: Out needs your body numbers."
                  />
                  {v.days.length ? (
                    <>
                      {words ? null : (
                        <p className="pg-legend" aria-hidden="true">
                          {food ? <span className="pg-legend__in">In</span> : null}
                          <span className="pg-legend__out">Out</span>
                          {food && v.inAvg !== null ? <span className="pg-legend__avg">avg {fmt(v.inAvg)}</span> : null}
                        </p>
                      )}
                      {food ? (
                        <p className="pg-cap">
                          {weekly ? "A pair a week, averaged" : "A pair a day"}
                          {words && v.inAvg !== null ? ` · avg In ${fmt(v.inAvg)}` : ""} · no In bar = not logged
                        </p>
                      ) : weekly ? (
                        <p className="pg-cap">A bar a week, averaged</p>
                      ) : null}
                    </>
                  ) : null}
                  {segmented}
                </>
              }
            />

            <RowGroup>
              <NavRow icon={I.list} title="Every day's In and Out" to="/progress/records/energy" />
            </RowGroup>

            {!food ? (
              <Notice secondary={{ label: "Food settings", onClick: () => go("/you/food") }}>
                Food logging is off, so expected against actual, the implied Out, the deficit&rsquo;s
                sources and coverage need a food log to work from.
              </Notice>
            ) : (
              <>
                <RowGroup heading="Expected vs actual">
                  <div className="k-chapter__leads k-chapter__leads--split">
                    <div className="k-chapter__lead">
                      <span className={`k-chapter__leadfig${v.expectedKg === null ? " is-empty" : ""}`}>
                        {v.expectedKg === null ? "–" : signed(v.expectedKg, 1)}
                        {v.expectedKg !== null ? <span className="k-chapter__leadunit">kg</span> : null}
                      </span>
                      <span className="k-chapter__leadlabel">expected from the food log</span>
                      <span className="k-chapter__leadnote">
                        <Tag tone="est" />
                      </span>
                    </div>
                    <div className="k-chapter__lead">
                      <span className={`k-chapter__leadfig${v.actualKg === null ? " is-empty" : ""}`}>
                        {v.actualKg === null ? "–" : signed(v.actualKg, 1)}
                        {v.actualKg !== null ? <span className="k-chapter__leadunit">kg</span> : null}
                      </span>
                      <span className="k-chapter__leadlabel">on the scale (trend)</span>
                      <span className="k-chapter__leadnote">
                        <Tag tone="neutral">trend</Tag>
                      </span>
                    </div>
                  </div>
                  {agree ? <Insight insight={agree} /> : null}
                </RowGroup>

                <RowGroup heading="Out implied by the scale">
                  <NavRow
                    kind="value"
                    title="Out implied by the scale"
                    value={
                      v.impliedOut === null ? (
                        "–"
                      ) : (
                        <>
                          ~{fmt(v.impliedOut)} <Tag tone="est" />
                        </>
                      )
                    }
                  />
                  <NavRow
                    kind="value"
                    title="Kaya’s estimate"
                    value={
                      v.outAvg === null ? (
                        "–"
                      ) : (
                        <>
                          ~{fmt(v.outAvg)} <Tag tone="est" />
                        </>
                      )
                    }
                  />
                  {implied ? <Insight insight={implied} /> : null}
                </RowGroup>

                <RowGroup heading="Where the deficit comes from">
                  {v.split ? (
                    <div className="pg-block">
                      <div className="pg-split" aria-hidden="true">
                        <span className="pg-split__food" style={{ flex: splitFood || 1 }} />
                        <span className="pg-split__work" style={{ flex: splitWork || 1 }} />
                      </div>
                      <p>
                        Food ~{fmt(splitFood)} · Work ~{fmt(splitWork)} kcal a day <Tag tone="est" />
                      </p>
                    </div>
                  ) : null}
                  {split ? <Insight insight={split} first={!v.split} /> : null}
                </RowGroup>
              </>
            )}

            {stall ? (
              <RowGroup heading="When the trend stalls">
                <Insight insight={stall} first />
              </RowGroup>
            ) : null}

            {food && cover ? (
              <RowGroup heading="Coverage">
                <Insight insight={cover} first />
                {v.of > 0 ? (
                  <div className="pg-block">
                    <ProgressBar
                      value={v.logged}
                      max={v.of}
                      ariaLabel={`${v.logged} of ${v.of} days logged`}
                    />
                  </div>
                ) : null}
              </RowGroup>
            ) : null}

            <RowGroup heading="Connected">
              <NavRow
                title="Food"
                icon={I.bowl}
                subtitle={food ? foodLine(S, end, w) : "Food logging is off"}
                to="/progress/food"
              />
              <NavRow title="Body" icon={I.scale} subtitle="What the loss is made of" to="/progress/body" />
            </RowGroup>
          </div>
        </Screen>
      </IonContent>
    </IonPage>
  );
}

